import { cvStatusEnum } from '@kiku/db/schema';
import { z } from 'zod';

import { ATS_PROVIDERS } from '@kiku/integrations/types';

// ── Candidate ───────────────────────────────────────────────────────────────

export const candidateSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable(),
  phoneCountryCode: z.string().nullable(),
  location: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  excludeFromSuggested: z.boolean(),
  atsExternalCandidateId: z.string().nullable(),
  atsProvider: z.enum(ATS_PROVIDERS).nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Candidate = z.infer<typeof candidateSchema>;

export const createCandidateInputSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  phoneCountryCode: z.string().max(5).optional(),
  atsExternalCandidateId: z.string().optional(),
  atsProvider: z.enum(ATS_PROVIDERS).optional()
});

export type CreateCandidateInput = z.infer<typeof createCandidateInputSchema>;

export class CandidateNotFoundError extends Error {
  constructor(id: string) {
    super(`Candidate ${id} not found`);
    this.name = 'CandidateNotFoundError';
  }
}

// ── CV ───────────────────────────────────────────────────────────────────────

export const CV_STATUSES = cvStatusEnum.enumValues;
export type CvStatus = (typeof CV_STATUSES)[number];

export const CV_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
] as const;
export type CvMimeType = (typeof CV_MIME_TYPES)[number];

export const cvExperienceSchema = z.object({
  company: z.string(),
  role: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  description: z.string().nullable()
});

export const cvEducationSchema = z.object({
  institution: z.string(),
  degree: z.string().nullable(),
  field: z.string().nullable(),
  year: z.string().nullable()
});

export const cvParsedDataSchema = z.object({
  summary: z.string().nullable(),
  skills: z.array(z.string()),
  experience: z.array(cvExperienceSchema),
  education: z.array(cvEducationSchema),
  languages: z.array(z.string()),
  certifications: z.array(z.string())
});

export type CvParsedData = z.infer<typeof cvParsedDataSchema>;

export const candidateProfileSchema = candidateSchema
  .pick({
    id: true,
    name: true,
    email: true,
    phoneNumber: true,
    phoneCountryCode: true,
    createdAt: true,
    updatedAt: true
  })
  .extend({
    cvStatus: z.enum(CV_STATUSES).nullable(),
    cvParsedData: cvParsedDataSchema.nullable(),
    cvSignedUrl: z.string().nullable(),
    dataToProcess: z.record(z.string(), z.unknown()).nullable()
  });

export type CandidateProfile = z.infer<typeof candidateProfileSchema>;

export const processCvJobPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  orgId: z.string().uuid(),
  candidateId: z.string().uuid()
});

export type ProcessCvJobPayload = z.infer<typeof processCvJobPayloadSchema>;

export class CvUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CvUploadError';
  }
}

export class CvParsingFailedError extends Error {
  constructor(candidateId: string) {
    super(`CV parsing failed for candidate ${candidateId}`);
    this.name = 'CvParsingFailedError';
  }
}

// ── Application ─────────────────────────────────────────────────────────────

export const applicationSchema = z.object({
  id: z.string(),
  candidateId: z.string(),
  jobId: z.string(),
  stageId: z.string(),
  createdById: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  candidate: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    phoneCountryCode: z.string().nullable()
  })
});

export type Application = z.infer<typeof applicationSchema>;

export const createApplicationInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    email: z.string().email(),
    phoneNumber: z.string().optional(),
    phoneCountryCode: z.string().max(5).optional()
  })
  .refine((d) => !!d.phoneNumber === !!d.phoneCountryCode, {
    message: 'Phone number and country code must both be provided or both be empty'
  });

export type CreateApplicationInput = z.infer<typeof createApplicationInputSchema>;

export const bulkImportRowSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  phoneNumber: z.string().min(1),
  phoneCountryCode: z.string().max(5)
});

export type BulkImportRow = z.infer<typeof bulkImportRowSchema>;

export const bulkImportInputSchema = z.object({
  candidates: z.array(bulkImportRowSchema).min(1).max(500)
});

export type BulkImportInput = z.infer<typeof bulkImportInputSchema>;

export const updateApplicationStageInputSchema = z.object({
  stageId: z.string()
});

export type UpdateApplicationStageInput = z.infer<typeof updateApplicationStageInputSchema>;

export const listApplicationsParamsSchema = z.object({
  stageId: z.string().optional(),
  page: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1))
    .optional()
    .default(1),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(500))
    .optional()
    .default(200)
});

export type ListApplicationsParams = z.infer<typeof listApplicationsParamsSchema>;

export class ApplicationNotFoundError extends Error {
  constructor(id: string) {
    super(`Application ${id} not found`);
    this.name = 'ApplicationNotFoundError';
  }
}

export class ApplicationConflictError extends Error {
  constructor(candidateId: string, jobId: string) {
    super(`Application already exists for candidate ${candidateId} on job ${jobId}`);
    this.name = 'ApplicationConflictError';
  }
}

// ── Extension lookup ─────────────────────────────────────────────────────────

export const extensionLookupQuerySchema = z.object({
  atsName: z.string(),
  externalCandidateId: z.string().optional(),
  externalJobId: z.string().optional(),
  externalApplicationId: z.string().optional()
});

export type ExtensionLookupQuery = z.infer<typeof extensionLookupQuerySchema>;

export const extensionLookupInputSchema = z.object({
  atsProvider: z.enum(ATS_PROVIDERS),
  externalCandidateId: z.string().optional(),
  externalJobId: z.string().optional(),
  externalApplicationId: z.string().optional()
});

export type ExtensionLookupInput = z.infer<typeof extensionLookupInputSchema>;

export const extensionLookupResultSchema = z.object({
  candidateName: z.string(),
  applicationId: z.string().uuid(),
  jobId: z.string().uuid(),
  deptSlug: z.string(),
  orgSlug: z.string(),
  hasScreeningTranscript: z.boolean()
});

export type ExtensionLookupResult = z.infer<typeof extensionLookupResultSchema>;
